import axios from 'axios';
import axiosInstance from './axiosInstance';
import { FEATURE_FLAGS } from '../constants';

// Activity Log (#735): the browser location is sent as two headers on PBA API calls only.
// Only while REACT_APP_LOCATION_HEADERS is on. Coordinates live in memory for this page session
// only (never localStorage, never a default, never from IP). Requests never wait for location:
// with no coordinates yet, they go out without the headers.

const PBA_SERVICE_ORIGINS = [process.env.REACT_APP_BASE_URL, process.env.REACT_APP_PDF_BASE_URL]
    .filter(Boolean)
    .map((origin) => origin.trim());
const NOTICE_SHOWN_KEY = 'pbaLocationNoticeShown';

let coords = null; // { latitude: '13.082700', longitude: '80.270700' }
let watchId = null;
let interceptorsAdded = false;
let noticeListener = null;

// PBA services only (pba75001py_* on the PBA API / PDF hosts). Never TagIT auth or third-party
// calls such as Google Input Tools, which go through the same global axios.
export const isPbaServiceUrl = (url) =>
    typeof url === 'string' && url.includes('/pba75001py_') && PBA_SERVICE_ORIGINS.some((origin) => url.startsWith(origin));

// Both headers or neither.
export const getClientLocationHeaders = () =>
    coords ? { 'X-Client-Latitude': coords.latitude, 'X-Client-Longitude': coords.longitude } : {};

const addLocationHeaders = (config) => {
    if (coords && isPbaServiceUrl(config.url)) {
        const headers = getClientLocationHeaders();
        Object.keys(headers).forEach((name) => {
            config.headers[name] = headers[name];
        });
    }
    return config;
};

// Added next to the existing interceptors without changing them. axiosInstance (loan / customer)
// does not inherit global interceptors, so it gets its own.
const ensureInterceptors = () => {
    if (interceptorsAdded) return;
    interceptorsAdded = true;
    axios.interceptors.request.use(addLocationHeaders);
    axiosInstance.interceptors.request.use(addLocationHeaders);
};

const showNoticeOnce = () => {
    try {
        if (sessionStorage.getItem(NOTICE_SHOWN_KEY)) return;
        sessionStorage.setItem(NOTICE_SHOWN_KEY, 'true');
    } catch (e) {
        // storage blocked: still show it this time
    }
    if (noticeListener) noticeListener();
};

const markUnavailable = () => {
    coords = null; // stop sending the headers
    showNoticeOnce();
};

// Called once the user is inside PBA. The browser shows its own permission prompt.
export function startClientLocation(onNotice) {
    if (!FEATURE_FLAGS.LOCATION_HEADERS) return;
    noticeListener = onNotice || null;
    if (watchId !== null) return;
    ensureInterceptors();
    // Geolocation needs HTTPS or localhost; skip the call (and its console warning) on plain HTTP.
    if (!window.isSecureContext || !navigator.geolocation) {
        markUnavailable();
        return;
    }
    watchId = navigator.geolocation.watchPosition(
        (position) => {
            coords = {
                latitude: position.coords.latitude.toFixed(6),
                longitude: position.coords.longitude.toFixed(6)
            };
        },
        (error) => {
            if (error?.code === 1 && watchId !== null) {
                navigator.geolocation.clearWatch(watchId); // permission denied: nothing more will come
                watchId = null;
            }
            markUnavailable();
        },
        { enableHighAccuracy: false, maximumAge: 300000, timeout: 10000 }
    );
}

// Stops tracking and clears the coordinates (logout or leaving the PBA layout).
export function stopClientLocation() {
    if (watchId !== null && navigator.geolocation) navigator.geolocation.clearWatch(watchId);
    watchId = null;
    coords = null;
    noticeListener = null;
}

// Logout: also lets the notice show again in the next login.
export function resetClientLocation() {
    if (!FEATURE_FLAGS.LOCATION_HEADERS) return;
    stopClientLocation();
    try {
        sessionStorage.removeItem(NOTICE_SHOWN_KEY);
    } catch (e) {
        // ignore
    }
}
